import _ from 'lodash';
import Immutable from 'seamless-immutable';
import RefData from 'ref-data';

import { credentialsBlueprints } from '../../common/enums';
import { getAssetRefWithFallback, getIntegrationGenericAssetRef } from './utils';

/**
 * Integration
 * @typedef {object} Credentials
 * @property {string} appId - an optional 3rd party application id
 * @property {string} apiKey - 3rd party api key
 * @property {string} appSecret - 3rd party application secret
 * @property {string} id - credentials id
 * @property {string} lastUpdated
 * @property {object} modifiedBy
 * @property {string} name a name of the credentials set
 * @property {status} status - a status of the credentials set
 * @property {refData} typeRef - a type of credentials set
 * @property {boolean} canBeDeleted - a flag to define if the credentials set can be removed
 * @property {string} consumerKey - Adobe consumer key
 * @property {string} consumerSecret - Adobe consumer secret
 * @property {string} url - Adobe domain name
 * @property {string} permissions
 * @property {string} namespace
 * @property {string} providerCopy
 * @property {string} supplementData

/**
 * Create an IntegrationCredentialsModel from provided data
 */

function create(integration = {}) {
  const result = _.clone(integration);
  result.status = _.isString(integration.status) ? RefData.Model.hydrate(integration.status) : integration.status;
  result.typeRef = _.isString(integration.typeRef) ? RefData.Model.hydrate(integration.typeRef) : integration.typeRef;
  result.blueprint = credentialsBlueprints()[result.typeRef.id] || {};

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend integration credentials object into a frontend IntegrationCredentialsModel
 * @param {Object} credentials - backend formatted credentials set
 * @returns {Credentials}
 */
function deserialize(credentialsSet) {
  return create(_.pick(credentialsSet, _keys));
}

/**
 * Transform an IntegrationCredentialsModel into a backend integration object
 * @returns {Object}
 */
function serialize(credentials) {
  const result = credentials.asMutable({ deep: true });
  result.status = credentials.status.id;
  result.typeRef = credentials.typeRef.id;

  delete result.blueprint;

  return _.pick(result, _keys);
}

/**
 * The IntegrationCredentialsModel prototype
 */
const model = {
  get isDisabled() {
    return this.status.id === RefData.Enum.STATUS.INTEGRATION.INACTIVE;
  },

  get statusLabel() {
    return this.status.label;
  },

  get assetRef() {
    return getIntegrationGenericAssetRef(
      this.typeRef.id,
      getAssetRefWithFallback({
        name:             this.name,
        icon:             this.blueprint.icon,
        height:           100,
        width:            120,
        fallbackFontSize: '72px',
      })
    );
  },
};

/**
 * An array of keys on the API Integration Credentials object that should be shared between the API requests and responses
 */
const _keys = [
  'appId',
  'apiKey',
  'appSecret',
  'canBeDeleted',
  'consumerKey',
  'consumerSecret',
  'id',
  'lastUpdated',
  'modifiedBy',
  'name',
  'status',
  'typeRef',
  'url',
  'permissions',
  'namespace',
  'providerCopy',
  'supplementData',
  'canBeDeleted',
];

export default {
  create,
  deserialize,
  serialize,
};
